import React, { useState, useEffect, useCallback } from 'react';
import { Card, Row, Col, Typography, Tag, Table, Spin, Button, Empty, Space, Select } from 'antd';
import { HomeOutlined, TeamOutlined, ThunderboltOutlined, ReloadOutlined, BuildOutlined } from '@ant-design/icons';
import { useSnackbar } from 'notistack';
import * as Sentry from "@sentry/react";
import { useTranslation } from 'react-i18next';
import PageLayout from '../components/PageLayout';
import EventStatsPanel from '../components/EventStatsPanel';
import { usePageLayout } from '../hooks/usePageLayout';
import { getDashboardData } from '../apiCalls';
import { isSuperAdmin } from '../utils/roleUtils';

const ACTION_COLORS = {
    LOGIN: 'blue',
    CREATE_USER: 'green',
    UPDATE_PRICING: 'orange',
    UPDATE_PDF_CONTENT: 'purple',
    UPDATE_PDF_LAYOUT: 'purple',
    UPDATE_EMAIL_CONFIG: 'cyan',
};

const StatCard = ({ icon, title, value, color }) => (
    <Card style={{ borderTop: `3px solid ${color}` }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
            <div style={{ fontSize: 32, color }}>{icon}</div>
            <div>
                <Typography.Text type="secondary" style={{ fontSize: 12, textTransform: 'uppercase', letterSpacing: 1 }}>
                    {title}
                </Typography.Text>
                <Typography.Title level={2} style={{ margin: 0, color }}>
                    {value ?? '—'}
                </Typography.Title>
            </div>
        </div>
    </Card>
);

const Dashboard = () => {
    const { t } = useTranslation();
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const pageLayoutProps = usePageLayout();
    const { role, propList } = pageLayoutProps;
    const { enqueueSnackbar } = useSnackbar();
    const [leaderboardSortBy, setLeaderboardSortBy] = useState('reuseRatePercent');
    const [leaderboardSortDirection, setLeaderboardSortDirection] = useState('desc');

    const ACTION_LABELS = {
        LOGIN: t('dashboard.actions.login'),
        CREATE_USER: t('dashboard.actions.createUser'),
        UPDATE_PRICING: t('dashboard.actions.pricingUpdate'),
        UPDATE_PDF_CONTENT: t('dashboard.actions.pdfContent'),
        UPDATE_PDF_LAYOUT: t('dashboard.actions.pdfLayout'),
        UPDATE_EMAIL_CONFIG: t('dashboard.actions.emailConfig'),
    };

    useEffect(() => {
        if (role && !isSuperAdmin(role)) {
            window.location.href = '/';
            return;
        }
    }, [role]);

    const fetchData = useCallback(async () => {
        if (!isSuperAdmin(role)) {
            return;
        }

        setLoading(true);
        try {
            const result = await getDashboardData();
            setData(result);
        } catch (err) {
            enqueueSnackbar(t('dashboard.error'), { variant: 'error' });
            Sentry.captureException(err);
        } finally {
            setLoading(false);
        }
    }, [enqueueSnackbar, role, t]);

    useEffect(() => { fetchData(); }, [fetchData]);

    const formatDate = (d) => {
        if (!d) return '—';
        return new Date(d).toLocaleString('en-GB', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' });
    };

    const getStaleness = (lastSnapshotAt) => {
        if (!lastSnapshotAt) {
            return { label: 'No data', color: 'default', daysOld: null };
        }

        const last = new Date(lastSnapshotAt);
        if (Number.isNaN(last.getTime())) {
            return { label: 'Unknown', color: 'default', daysOld: null };
        }

        const daysOld = Math.floor((Date.now() - last.getTime()) / (24 * 60 * 60 * 1000));
        if (daysOld <= 7) return { label: 'Fresh', color: 'green', daysOld };
        if (daysOld <= 30) return { label: 'Aging', color: 'gold', daysOld };
        return { label: 'Stale', color: 'red', daysOld };
    };

    const leaderboardRows = Array.isArray(data?.roofAssistLeaderboard)
        ? [...data.roofAssistLeaderboard].sort((a, b) => {
            const left = Number(a?.[leaderboardSortBy] || 0);
            const right = Number(b?.[leaderboardSortBy] || 0);
            if (leaderboardSortDirection === 'asc') return left - right;
            return right - left;
        })
        : [];

    const exportRoofAssistLeaderboardCsv = () => {
        const rows = Array.isArray(data?.roofAssistLeaderboard) ? data.roofAssistLeaderboard : [];
        if (rows.length === 0) return;

        const escapeCell = (value) => `"${String(value ?? '').replace(/"/g, '""')}"`;
        const headers = ['propertyID', 'companyName', 'reuseRatePercent', 'totalRuns', 'totalReuse', 'runs30d', 'reuses30d', 'snapshotCount', 'lastSnapshotAt'];
        const csvRows = [headers, ...rows.map((row) => [
            row.propertyID,
            row.companyName,
            row.reuseRatePercent,
            row.totalRuns,
            row.totalReuse,
            row.runs30d,
            row.reuses30d,
            row.snapshotCount,
            row.lastSnapshotAt || '',
        ])];

        const csv = csvRows.map((row) => row.map(escapeCell).join(',')).join('\n');
        const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = 'roof-assist-leaderboard.csv';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
    };

    const activityColumns = [
        {
            title: t('dashboard.columns.time'),
            dataIndex: 'timestamp',
            width: 160,
            render: formatDate,
        },
        {
            title: t('dashboard.columns.user'),
            dataIndex: 'userEmail',
            ellipsis: true,
        },
        {
            title: t('dashboard.columns.action'),
            dataIndex: 'action',
            width: 160,
            render: (action) => (
                <Tag color={ACTION_COLORS[action] || 'default'}>
                    {ACTION_LABELS[action] || action}
                </Tag>
            ),
        },
        {
            title: t('dashboard.columns.property'),
            dataIndex: 'propertyId',
            width: 130,
            render: (id) => {
                if (!id) return <Typography.Text type="secondary">—</Typography.Text>;
                const found = data?.propertyStats?.find(p => p.propertyID === id);
                return found ? found.companyName : id;
            },
        },
    ];

    const propertyColumns = [
        {
            title: t('dashboard.columns.property'),
            dataIndex: 'companyName',
            render: (name) => <Typography.Text strong>{name}</Typography.Text>,
        },
        {
            title: t('dashboard.columns.pricingUpdates'),
            dataIndex: 'totalPricingUpdates',
            width: 140,
            align: 'center',
            render: (v) => <Tag color="orange">{v}</Tag>,
        },
        {
            title: t('dashboard.columns.lastUpdated'),
            dataIndex: 'lastPricingUpdate',
            width: 180,
            render: formatDate,
        },
        {
            title: t('dashboard.columns.updatedBy'),
            dataIndex: 'lastUpdatedByName',
            ellipsis: true,
            render: (name) => name || <Typography.Text type="secondary">—</Typography.Text>,
        },
    ];

    const loginColumns = [
        {
            title: t('dashboard.columns.user'),
            dataIndex: '_id',
            ellipsis: true,
        },
        {
            title: t('dashboard.columns.logins7Days'),
            dataIndex: 'loginCount',
            width: 130,
            align: 'center',
            render: (v) => <Tag color="blue">{v}</Tag>,
        },
        {
            title: t('dashboard.columns.lastLogin'),
            dataIndex: 'lastLogin',
            width: 180,
            render: formatDate,
        },
    ];

    const roofAssistColumns = [
        {
            title: 'Property',
            dataIndex: 'companyName',
            render: (name, row) => (
                <Space direction="vertical" size={0}>
                    <Typography.Text strong>{name}</Typography.Text>
                    <Typography.Text type="secondary" style={{ fontSize: 12 }}>{row.propertyID}</Typography.Text>
                </Space>
            ),
        },
        {
            title: 'Reuse rate',
            dataIndex: 'reuseRatePercent',
            width: 120,
            align: 'right',
            render: (value) => <Tag color={Number(value) >= 40 ? 'green' : (Number(value) >= 20 ? 'gold' : 'default')}>{Number(value || 0)}%</Tag>,
        },
        {
            title: 'Status',
            dataIndex: 'lastSnapshotAt',
            width: 140,
            render: (value) => {
                const status = getStaleness(value);
                if (status.daysOld == null) return <Tag color={status.color}>{status.label}</Tag>;
                return <Tag color={status.color}>{`${status.label} (${status.daysOld}d)`}</Tag>;
            },
        },
        {
            title: 'Runs (30d)',
            dataIndex: 'runs30d',
            width: 110,
            align: 'right',
        },
        {
            title: 'Reuses (30d)',
            dataIndex: 'reuses30d',
            width: 120,
            align: 'right',
        },
        {
            title: 'Runs (all)',
            dataIndex: 'totalRuns',
            width: 100,
            align: 'right',
        },
        {
            title: 'Snapshots',
            dataIndex: 'snapshotCount',
            width: 100,
            align: 'right',
        },
    ];

    return (
        <PageLayout {...pageLayoutProps}>
            <div style={{ maxWidth: 1200, margin: '0 auto' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24 }}>
                    <Typography.Title level={2} style={{ margin: 0 }}>{t('dashboard.title')}</Typography.Title>
                    <Button icon={<ReloadOutlined />} onClick={fetchData} loading={loading}>
                        {t('common.refresh')}
                    </Button>
                </div>

                {loading && !data ? (
                    <div style={{ textAlign: 'center', padding: 80 }}>
                        <Spin size="large" />
                    </div>
                ) : !data ? (
                    <Empty description={t('dashboard.noData')} />
                ) : (
                    <>
                        {/* Summary cards */}
                        <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
                            <Col xs={24} sm={8}>
                                <StatCard
                                    icon={<BuildOutlined />}
                                    title={t('dashboard.properties')}
                                    value={data.summary.totalProperties}
                                    color="#1890ff"
                                />
                            </Col>
                            <Col xs={24} sm={8}>
                                <StatCard
                                    icon={<TeamOutlined />}
                                    title={t('dashboard.users')}
                                    value={data.summary.totalUsers}
                                    color="#52c41a"
                                />
                            </Col>
                            <Col xs={24} sm={8}>
                                <StatCard
                                    icon={<ThunderboltOutlined />}
                                    title={t('dashboard.actions30Days')}
                                    value={data.summary.actionsLast30Days}
                                    color="#fa8c16"
                                />
                            </Col>
                        </Row>

                        {/* Action breakdown */}
                        <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
                            <Col xs={24} md={8}>
                                <Card title={t('dashboard.activityBreakdown')} style={{ height: '100%' }}>
                                    {data.actionBreakdown.length === 0 ? (
                                        <Typography.Text type="secondary">{t('dashboard.noActivity')}</Typography.Text>
                                    ) : (
                                        data.actionBreakdown.map(item => (
                                            <div key={item._id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>
                                                <Tag color={ACTION_COLORS[item._id] || 'default'}>
                                                    {ACTION_LABELS[item._id] || item._id}
                                                </Tag>
                                                <Typography.Text strong>{item.count}</Typography.Text>
                                            </div>
                                        ))
                                    )}
                                </Card>
                            </Col>

                            {/* Recent logins */}
                            <Col xs={24} md={16}>
                                <Card title={t('dashboard.userLogins')}>
                                    <Table
                                        dataSource={data.recentLogins}
                                        columns={loginColumns}
                                        rowKey="_id"
                                        size="small"
                                        pagination={false}
                                        scroll={{ x: 'max-content' }}
                                        locale={{ emptyText: t('dashboard.noLogins') }}
                                    />
                                </Card>
                            </Col>
                        </Row>

                        {/* Property pricing history */}
                        <Card title={<span><HomeOutlined style={{ marginRight: 8 }} />{t('dashboard.pricingByProperty')}</span>} style={{ marginBottom: 24 }}>
                            <Table
                                dataSource={data.propertyStats}
                                columns={propertyColumns}
                                rowKey="propertyID"
                                size="small"
                                pagination={false}
                                scroll={{ x: 'max-content' }}
                                locale={{ emptyText: t('dashboard.noProperties') }}
                            />
                        </Card>

                        {/* Recent activity feed */}
                        <Card title={t('dashboard.recentActivity')}>
                            <Table
                                dataSource={data.recentActivity}
                                columns={activityColumns}
                                rowKey="_id"
                                size="small"
                                pagination={false}
                                scroll={{ x: 'max-content' }}
                                locale={{ emptyText: t('dashboard.noActivityYet') }}
                            />
                        </Card>

                        <Card
                            style={{ marginTop: 24 }}
                            title="Roof Assist Leaderboard"
                            extra={(
                                <Space wrap>
                                    <Select
                                        size="small"
                                        value={leaderboardSortBy}
                                        style={{ width: 170 }}
                                        onChange={setLeaderboardSortBy}
                                        options={[
                                            { value: 'reuseRatePercent', label: 'Sort: Reuse rate' },
                                            { value: 'runs30d', label: 'Sort: Runs (30d)' },
                                            { value: 'reuses30d', label: 'Sort: Reuses (30d)' },
                                            { value: 'totalRuns', label: 'Sort: Runs (all)' },
                                            { value: 'snapshotCount', label: 'Sort: Snapshots' },
                                        ]}
                                    />
                                    <Select
                                        size="small"
                                        value={leaderboardSortDirection}
                                        style={{ width: 105 }}
                                        onChange={setLeaderboardSortDirection}
                                        options={[
                                            { value: 'desc', label: 'Desc' },
                                            { value: 'asc', label: 'Asc' },
                                        ]}
                                    />
                                    <Button size="small" onClick={exportRoofAssistLeaderboardCsv} disabled={!leaderboardRows.length}>
                                        Export CSV
                                    </Button>
                                </Space>
                            )}
                        >
                            <Table
                                dataSource={leaderboardRows}
                                columns={roofAssistColumns}
                                rowKey="propertyID"
                                size="small"
                                pagination={false}
                                scroll={{ x: 'max-content' }}
                                locale={{ emptyText: 'No roof assistant usage has been recorded yet.' }}
                            />
                        </Card>

                        {/* Super-admin: aggregated event tracking stats */}
                        {isSuperAdmin(role) && (
                            <div style={{ marginTop: 24 }}>
                                <EventStatsPanel propList={propList} />
                            </div>
                        )}
                    </>
                )}
            </div>
        </PageLayout>
    );
};

export default Dashboard;
